import React, { useEffect, useState, useCallback, useRef } from 'react';
import { DollarSign, Download, Loader2, MoreHorizontal, Plus } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { orgApi } from './phase567Api';
import { api, isSpendApprovalError, downloadWithAuth } from './api';
import { useAuth } from './AuthContext';
import { isExternalRole } from './externalRoles';
import PermissionsTab from './org/PermissionsTab';
import IncidentTab from './org/IncidentTab';
import MoveDealsTab from './org/MoveDealsTab';
import LoanProgramsTab from './org/LoanProgramsTab';
import QualificationDefaultsTab from './org/QualificationDefaultsTab';
import NotificationsTab from './org/NotificationsTab';
import AiUsageTab from './org/AiUsageTab';
import FeaturesTab from './org/FeaturesTab';
import ChangeRoleDialog from './org/ChangeRoleDialog';
import EditMemberDialog from './org/EditMemberDialog';
import EditBranchDialog from './org/EditBranchDialog';
import EditTeamDialog from './org/EditTeamDialog';
import EditPlanDialog from './org/EditPlanDialog';
import CommissionCalculator from './org/CommissionCalculator';
import TeamMembersDialog from './org/TeamMembersDialog';
import BulkInviteDialog from './org/BulkInviteDialog';
import { ROLE_LABELS, INVITABLE_ROLES } from './org/roles';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../components/ui/alert-dialog';
import { useTabParam } from './shared/useTabParam';
import { errorMessage } from './errors';
import MoneyInput from './shared/MoneyInput';
import NumberInput from './shared/NumberInput';
import { formatDate } from './kit/time';

const fmt$ = (n) => n ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '—';

// Ticket 74: one status chip for members and invitations.
const STATUS_CHIP = {
  active: { label: 'Active', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  invited: { label: 'Invited', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  suspended: { label: 'Suspended', cls: 'bg-red-50 text-red-700 border-red-200' },
};
export function StatusChip({ status }) {
  const key = String(status || 'active').toLowerCase();
  const c = STATUS_CHIP[key] || { label: key.charAt(0).toUpperCase() + key.slice(1), cls: 'bg-slate-50 text-slate-600 border-slate-200' };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${c.cls}`} data-testid={`status-chip-${key}`}>
      {c.label}
    </span>
  );
}

// Right-aligned ⋮ menu used by every row on this page. `items` is a list of
// { key, label, onClick, danger?, testid? }; an empty list renders nothing.
function RowMenu({ items, testid }) {
  if (!items.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="w-7 h-7 rounded hover:bg-slate-100 flex items-center justify-center text-slate-500" title="Actions" data-testid={testid}>
          <MoreHorizontal size={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {items.map((it) => (
          <DropdownMenuItem key={it.key} onClick={it.onClick} data-testid={it.testid}
            className={it.danger ? 'text-red-600 focus:text-red-600 focus:bg-red-50' : undefined}>
            {it.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function OrgSettingsPage() {
  const { user, checkAuth } = useAuth();
  const canInvite = user?.membership_role === 'owner' || user?.membership_role === 'admin';
  const perms = user?.permissions || [];
  // Ticket 202: staff.manage lets a non-admin invite and remove staff; role, suspend and profile edits stay with admins.
  const canManageStaff = canInvite || perms.includes('staff.manage');
  const canKillSessions = perms.includes('incident.kill_sessions');
  const canKillLinks = perms.includes('incident.kill_links');
  const canManageQual = perms.includes('qualification.manage');
  // Ticket 70: exactly one owner per workspace; only the owner (or the platform
  // super-admin) can hand the seat to someone else.
  const canTransfer = user?.membership_role === 'owner' || !!user?.is_super_admin;
  // Ticket 197: the AI usage meter is the owner's (Ken sees his own usage without super-admin).
  const canSeeAiUsage = canTransfer;
  // Ticket 202: owner feature switches; the server gates on the owner-only features.manage action.
  const canManageFeatures = perms.includes('features.manage') || !!user?.is_super_admin;
  const [ownerTarget, setOwnerTarget] = useState(null);
  const [transferBusy, setTransferBusy] = useState(false);
  const [tab, setTab] = useTabParam('members', { valid: ['members', 'portal', 'branches', 'teams', 'plans', 'commissions', 'permissions', 'incident', 'move', 'programs', 'qualdefaults', 'notifications', 'usage', 'features'] });
  // Ticket 94: the "Move deals" tab exists only inside a personal workspace
  // whose owner also holds a seat in at least one company.
  const [seats, setSeats] = useState([]);
  const activeSeat = seats.find((s) => s.is_active);
  const companySeats = seats.filter((s) => s.kind !== 'personal');
  const canMoveDeals = activeSeat?.kind === 'personal' && companySeats.length > 0;
  useEffect(() => {
    // A stale ?tab=move link inside a company workspace falls back to Team.
    if (tab === 'move' && seats.length > 0 && !canMoveDeals) setTab('members');
    // Same for ?tab=features without the owner-only permission.
    if (tab === 'features' && user && !canManageFeatures) setTab('members');
  }, [tab, seats, canMoveDeals, canManageFeatures, user, setTab]);
  const [killTarget, setKillTarget] = useState(null);
  const [killBusy, setKillBusy] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null); // member whose seat is being suspended/reactivated
  const [statusBusy, setStatusBusy] = useState(false);
  const [roleTarget, setRoleTarget] = useState(null); // member whose role is being changed (ticket 202)
  const [profileTarget, setProfileTarget] = useState(null); // member whose profile is being edited (ticket 202)
  const [branchTarget, setBranchTarget] = useState(null); // branch being edited (ticket 202)
  const [teamTarget, setTeamTarget] = useState(null); // team being edited (ticket 202)
  const [planTarget, setPlanTarget] = useState(null); // commission plan being edited (ticket 202)
  const [membersTeamId, setMembersTeamId] = useState(null); // team whose roster is open; the live row is looked up so adds show at once
  // Ticket 109: remove a member. `removeImpact` is the open-deal count from the
  // impact endpoint (null while loading or if it could not be fetched).
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeImpact, setRemoveImpact] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const removeSeq = useRef(0); // which openRemove call owns the dialog; stale impact replies are dropped
  const [members, setMembers] = useState([]);
  const [portalSeats, setPortalSeats] = useState([]); // borrower / realtor / title-escrow logins (ticket 302)
  const [invitations, setInvitations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [plans, setPlans] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  // Ticket 202: the Permissions grid reports unsaved edits so a tab switch can ask first.
  const [permsDirty, setPermsDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  const [addForm, setAddForm] = useState({});
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  // Ticket 202: browsers without a month picker show a text box; only a full YYYY-MM reloads the list.
  const [periodDraft, setPeriodDraft] = useState(period);
  const pickPeriod = (v) => { setPeriodDraft(v); if (/^\d{4}-(0[1-9]|1[0-2])$/.test(v)) setPeriod(v); };
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, t, p, r, m, ext, inv, s] = await Promise.all([
        orgApi.listBranches(), orgApi.listTeams(),
        orgApi.listPlans(),
        orgApi.listRecords({ period }).catch(() => ({ items: [] })),
        api.get('/admin/users').then(({ data }) => data).catch(() => []),
        api.get('/admin/users?external=1').then(({ data }) => data).catch(() => []),
        api.get('/invitations').then(({ data }) => data.items || []).catch(() => []),
        api.get('/tenants/memberships').then(({ data }) => data.items || []).catch(() => []),
      ]);
      setMembers(m || []);
      setPortalSeats(ext || []);
      setInvitations(inv || []);
      setSeats(s || []);
      setBranches(b.items || []);
      setTeams(t.items || []);
      setPlans(p.items || []);
      setRecords(r.items || []);
    } catch { toast({ title: 'Load failed' }); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      if (tab === 'members') {
        const { data } = await api.post('/invitations', { email: addForm.email, role: addForm.role || 'loan_officer' });
        toast({ title: 'Invitation sent', description: data.email_sent ? `Emailed ${data.email}.` : `Created for ${data.email}, but the email could not be sent — check email settings.` });
        setAddOpen(false); setAddForm({}); load();
        return;
      }
      if (tab === 'branches') await orgApi.createBranch(addForm);
      if (tab === 'teams') await orgApi.createTeam(addForm);
      if (tab === 'plans') await orgApi.createPlan({
        ...addForm,
        bps: addForm.bps == null || addForm.bps === '' ? null : Number(addForm.bps),
        cap_per_deal: addForm.cap_per_deal == null || addForm.cap_per_deal === '' ? null : Number(addForm.cap_per_deal),
        deductions_pct: addForm.deductions_pct ? Number(addForm.deductions_pct) : 0,
      });
      toast({ title: 'Created' });
      setAddOpen(false); setAddForm({}); load();
    } catch (e) {
      // Ticket 167: the interceptor already toasted the approval message.
      if (!isSpendApprovalError(e)) toast({ title: 'Failed', description: errorMessage(e) });
    }
  };

  const del = async (kind, id) => {
    if (!(await confirmDialog({ title: `Delete this ${kind}?`, confirmText: 'Delete', destructive: true }))) return;
    try {
      if (kind === 'branch') await orgApi.deleteBranch(id);
      if (kind === 'team') await orgApi.deleteTeam(id);
      if (kind === 'plan') await orgApi.deletePlan(id);
      load();
    } catch { toast({ title: 'Failed' }); }
  };

  const revokeInvite = async (id) => {
    if (!(await confirmDialog({ title: 'Revoke this invitation?', description: 'The invite link stops working immediately.', confirmText: 'Revoke', destructive: true }))) return;
    try {
      await api.delete(`/invitations/${id}`);
      load();
    } catch { toast({ title: 'Failed' }); }
  };

  // Ticket 109: the raw token is not stored, so resend rotates it and emails the new link.
  const resendInvite = async (inv) => {
    try {
      const { data } = await api.post(`/invitations/${inv.invitation_id}/resend`);
      toast(data?.email_sent === false
        ? { title: 'Link renewed, but the email failed to send', description: `Check email settings, then try again for ${inv.email}.` }
        : { title: `Sent to ${data?.email || inv.email}` });
      await load();
    } catch (e) {
      if (!isSpendApprovalError(e)) toast({ title: 'Failed', description: errorMessage(e) });
    }
  };

  const transferOwnership = async () => {
    if (!ownerTarget) return;
    setTransferBusy(true);
    try {
      await orgApi.transferOwnership(ownerTarget.user_id);
      toast({ title: 'Ownership transferred', description: `${ownerTarget.name} is now the owner.` });
      await load();
      await checkAuth();
    } catch (e) {
      toast({ title: 'Transfer failed', description: errorMessage(e) });
    } finally {
      setTransferBusy(false);
      setOwnerTarget(null);
    }
  };

  // Ticket 74: suspend / reactivate a seat via PATCH /admin/users (membership status).
  const nextStatusFor = (m) => ((m?.membership_status || 'active') === 'active' ? 'suspended' : 'active');
  const setMemberStatus = async () => {
    if (!statusTarget) return;
    const next = nextStatusFor(statusTarget);
    setStatusBusy(true);
    try {
      await api.patch(`/admin/users/${statusTarget.user_id}`, { status: next });
      toast({ title: next === 'suspended' ? 'Seat suspended' : 'Seat reactivated', description: `${statusTarget.name || statusTarget.email} is now ${next}.` });
      await load();
    } catch (e) {
      toast({ title: 'Failed', description: errorMessage(e) });
    } finally {
      setStatusBusy(false);
      setStatusTarget(null);
    }
  };

  const openRemove = async (m) => {
    const seq = ++removeSeq.current;
    setRemoveTarget(m);
    setRemoveImpact(null);
    try {
      const { data } = await api.get(`/admin/users/${m.user_id}/membership/impact`);
      if (seq === removeSeq.current) setRemoveImpact(data?.open_assignments ?? 0);
    } catch { /* the count is advisory; removal still works without it */ }
  };
  const removeMember = async () => {
    if (!removeTarget) return;
    setRemoveBusy(true);
    try {
      const { data } = await api.delete(`/admin/users/${removeTarget.user_id}/membership`);
      const left = data?.open_assignments || 0;
      toast({
        title: 'Member removed',
        description: `${removeTarget.name || removeTarget.email} no longer has access.${left ? ` ${left} deal${left === 1 ? ' is' : 's are'} still assigned to or owned by them.` : ''}`,
      });
      await load();
    } catch (e) {
      toast({ title: 'Failed', description: errorMessage(e) });
    } finally {
      setRemoveBusy(false);
      setRemoveTarget(null);
    }
  };

  const memberMenu = (m) => {
    const status = m.membership_status || 'active';
    const isSelf = m.user_id === user?.user_id;
    const external = isExternalRole(m.membership_role);
    const items = [];
    if (canTransfer && m.membership_role !== 'owner' && !external && status === 'active') {
      items.push({ key: 'owner', label: 'Make owner', onClick: () => setOwnerTarget(m), testid: `org-make-owner-${m.user_id}` });
    }
    if (canInvite && m.membership_role !== 'owner' && !isSelf) {
      items.push(status === 'active'
        ? { key: 'suspend', label: 'Suspend', onClick: () => setStatusTarget(m), testid: `org-suspend-${m.user_id}`, danger: true }
        : { key: 'reactivate', label: 'Reactivate', onClick: () => setStatusTarget(m), testid: `org-reactivate-${m.user_id}` });
    }
    // Ticket 202: profile edits touch an owner seat only from an owner (or super-admin), like the server.
    if (canInvite && !external && (m.membership_role !== 'owner' || canTransfer)) {
      items.push({ key: 'profile', label: 'Edit profile', onClick: () => setProfileTarget(m), testid: `org-edit-member-${m.user_id}` });
    }
    // Ticket 202: owner seats move only through Make owner; borrower/realtor seats are external.
    if (canInvite && m.membership_role !== 'owner' && !isSelf && !external) {
      items.push({ key: 'role', label: 'Change role', onClick: () => setRoleTarget(m), testid: `org-change-role-${m.user_id}` });
    }
    // Ticket 109: owner seats need an owner (or super-admin) caller, never your own row.
    // A staff.manage manager removes staff, never an admin, owner, borrower or realtor seat (the server enforces it too).
    const protectedSeat = m.membership_role === 'owner' || m.membership_role === 'admin' || isExternalRole(m.membership_role);
    if (canManageStaff && !isSelf && (m.membership_role !== 'owner' || canTransfer) && (canInvite || !protectedSeat)) {
      items.push({ key: 'remove', label: 'Remove from workspace', onClick: () => openRemove(m), testid: `org-remove-${m.user_id}`, danger: true });
    }
    if (canKillSessions) {
      items.push({ key: 'kill', label: 'Sign out everywhere', onClick: () => setKillTarget(m), testid: `org-kill-sessions-${m.user_id}`, danger: true });
    }
    return items;
  };
  // Ticket 202: who can manage a branch or team (active seats, no borrower/realtor).
  const staff = members.filter((m) => (m.membership_status || 'active') === 'active' && !isExternalRole(m.membership_role));
  const nameOf = (uid) => { const m = members.find((x) => x.user_id === uid); return m ? (m.name || m.email) : null; };
  const showMemberActions = canTransfer || canManageStaff || canKillSessions;

  const exportCsv = async () => {
    setExporting(true);
    try { await downloadWithAuth(orgApi.payrollCsvPath(period), `payroll_${period}.csv`); }
    finally { setExporting(false); }
  };

  const killMemberSessions = async () => {
    if (!killTarget) return;
    setKillBusy(true);
    try {
      await orgApi.killMemberSessions(killTarget.user_id);
      toast({ title: 'Signed out', description: `${killTarget.name} was signed out everywhere.` });
    } catch (e) {
      if (e?.response?.status === 404) {
        toast({ title: 'Not available', description: "This member can't be signed out from here." });
      } else if (e?.response?.data?.detail?.code !== 'permission_denied') {
        toast({ title: 'Failed', description: errorMessage(e) });
      }
    } finally {
      setKillBusy(false);
      setKillTarget(null);
    }
  };

  return (
    <div className="p-4 md:p-8" data-testid="org-settings-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }} data-testid="org-settings-title">Organization</h1>
          <p className="text-slate-500 mt-1" data-testid="org-settings-subtitle">Branches · teams · commission plans · payroll export</p>
        </div>
        {tab !== 'commissions' && tab !== 'permissions' && tab !== 'incident' && tab !== 'move' && tab !== 'programs' && tab !== 'qualdefaults' && tab !== 'notifications' && tab !== 'usage' && tab !== 'features' && tab !== 'portal' && (tab !== 'members' || canManageStaff) && (
          <div className="flex items-center gap-2">
            {tab === 'members' && (
              <button onClick={() => setBulkOpen(true)} data-testid="org-bulk-invite-btn" className="btn-outline">
                Invite several
              </button>
            )}
            <button onClick={() => setAddOpen(true)} data-testid="org-add-btn" className="btn-gold">
              <Plus size={16} /> {tab === 'members' ? 'Invite member' : `New ${tab.slice(0, -1)}`}
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-1 mb-6 border-b border-slate-100 overflow-x-auto">
        {[
          { k: 'members', l: 'Team', c: members.length + invitations.length },
          // DECIDE:c65473a9-ab32-4ee1-88fe-a89125919ee5 — portal logins live on their own tab for now; drop the tab if Ken picks "hide".
          { k: 'portal', l: 'Portal access', c: portalSeats.length },
          { k: 'branches', l: 'Branches', c: branches.length },
          { k: 'teams', l: 'Teams', c: teams.length },
          { k: 'plans', l: 'Commission Plans', c: plans.length },
          { k: 'commissions', l: 'Commissions', c: records.length },
          { k: 'permissions', l: 'Permissions' },
          { k: 'notifications', l: 'Notifications' },
          ...(canManageFeatures ? [{ k: 'features', l: 'Features' }] : []),
          ...(canSeeAiUsage ? [{ k: 'usage', l: 'AI usage' }] : []),
          ...(canManageQual ? [{ k: 'programs', l: 'Loan programs' }, { k: 'qualdefaults', l: 'Qualification defaults' }] : []),
          ...(canKillSessions || canKillLinks ? [{ k: 'incident', l: 'Incident' }] : []),
          ...(canMoveDeals ? [{ k: 'move', l: 'Move deals' }] : []),
        ].map(t => (
          <button key={t.k} onClick={() => (tab === 'permissions' && permsDirty && t.k !== 'permissions' ? setPendingTab(t.k) : setTab(t.k))}
            data-testid={`org-tab-${t.k}`}
            className={`px-4 py-2.5 text-sm border-b-2 -mb-px transition whitespace-nowrap ${
              tab === t.k ? 'font-medium' : 'text-slate-500 border-transparent'
            }`}
            style={tab === t.k ? { borderColor: '#070B5F', color: '#070B5F' } : undefined}>
            {t.l} <span className="text-xs text-slate-400">{t.c}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden p-16 flex items-center justify-center text-slate-400">
          <Loader2 className="animate-spin mr-2" size={20} /> Loading...
        </div>
      ) : tab === 'members' ? (
        <div className="space-y-6" data-testid="org-members">
          <div className="relative bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <th className="px-4 py-3">Member</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th>
                  {showMemberActions && <th className="px-4 py-3 text-right"><span className="sr-only">Actions</span></th>}
                </tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.user_id} className={`border-b border-slate-50 ${(m.membership_status || 'active') === 'suspended' ? 'opacity-60' : ''}`} data-testid={`org-member-${m.user_id}`}>
                    <td className="px-4 py-3"><div className="font-medium" style={{ color: '#070B5F' }}>{m.name}</div><div className="text-xs text-slate-500">{m.email}</div></td>
                    <td className="px-4 py-3">{ROLE_LABELS[m.membership_role] || m.membership_role || m.role}</td>
                    <td className="px-4 py-3"><StatusChip status={m.membership_status || 'active'} /></td>
                    {showMemberActions && (
                      <td className="px-4 py-3 text-right">
                        <RowMenu items={memberMenu(m)} testid={`org-member-menu-${m.user_id}`} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Pending invitations</div>
            {invitations.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 text-center text-slate-400 text-sm">No pending invitations</div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                {invitations.map(i => (
                  <div key={i.invitation_id} className="flex items-center justify-between px-4 py-3 border-b border-slate-50" data-testid={`org-invitation-${i.invitation_id}`}>
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="text-sm font-medium" style={{ color: '#070B5F' }}>{i.email}</div>
                        <div className="text-xs text-slate-500">{ROLE_LABELS[i.role] || i.role} · expires {formatDate(i.expires_at)}</div>
                      </div>
                      <StatusChip status="invited" />
                    </div>
                    {canManageStaff && (
                      <RowMenu testid={`org-invitation-menu-${i.invitation_id}`} items={[
                        { key: 'resend', label: 'Resend invite', onClick: () => resendInvite(i), testid: `org-resend-${i.invitation_id}` },
                        { key: 'revoke', label: 'Revoke invitation', onClick: () => revokeInvite(i.invitation_id), testid: `org-revoke-${i.invitation_id}`, danger: true },
                      ]} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : tab === 'portal' ? (
        <div className="relative bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden overflow-x-auto" data-testid="org-portal">
          {portalSeats.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm" data-testid="org-portal-empty">No borrower, realtor, or title/escrow portal logins yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <th className="px-4 py-3">Member</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Status</th>
                  {showMemberActions && <th className="px-4 py-3 text-right"><span className="sr-only">Actions</span></th>}
                </tr>
              </thead>
              <tbody>
                {portalSeats.map(m => (
                  <tr key={m.user_id} className={`border-b border-slate-50 ${(m.membership_status || 'active') === 'suspended' ? 'opacity-60' : ''}`} data-testid={`org-portal-${m.user_id}`}>
                    <td className="px-4 py-3"><div className="font-medium" style={{ color: '#070B5F' }}>{m.name}</div><div className="text-xs text-slate-500">{m.email}</div></td>
                    <td className="px-4 py-3">{ROLE_LABELS[m.membership_role] || m.membership_role || m.role}</td>
                    <td className="px-4 py-3"><StatusChip status={m.membership_status || 'active'} /></td>
                    {showMemberActions && (
                      <td className="px-4 py-3 text-right">
                        <RowMenu items={memberMenu(m)} testid={`org-portal-menu-${m.user_id}`} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : tab === 'branches' ? (
        <List items={branches} kind="branch" onDel={del} onEdit={setBranchTarget} render={(b) => (
          <>
            <div className="text-sm font-medium" style={{ color: '#070B5F' }}>{b.name}</div>
            <div className="text-xs text-slate-500">{[b.city, b.state].filter(Boolean).join(', ') || '—'}{b.nmls ? ` · NMLS ${b.nmls}` : ''}{nameOf(b.manager_user_id) ? ` · Manager ${nameOf(b.manager_user_id)}` : ''}</div>
          </>
        )} />
      ) : tab === 'teams' ? (
        <List items={teams} kind="team" onDel={del} onEdit={setTeamTarget} extraItems={(t) => [{ key: 'members', label: 'Manage members', onClick: () => setMembersTeamId(t.team_id), testid: `team-members-${t.team_id}` }]} render={(t) => (
          <>
            <div className="text-sm font-medium" style={{ color: '#070B5F' }}>{t.name}</div>
            <div className="text-xs text-slate-500">
              {t.kind} · {t.member_user_ids?.length || 0} member(s)
              {branches.find((b) => b.branch_id === t.branch_id) ? ` · ${branches.find((b) => b.branch_id === t.branch_id).name}` : ''}
              {nameOf(t.manager_user_id) ? ` · Manager ${nameOf(t.manager_user_id)}` : ''}
            </div>
          </>
        )} />
      ) : tab === 'plans' ? (
        <List items={plans} kind="plan" onDel={del} onEdit={setPlanTarget} render={(p) => (
          <>
            <div className="text-sm font-medium" style={{ color: '#070B5F' }}>{p.name}</div>
            <div className="text-xs text-slate-500">
              {p.division} · {p.bps ? `${p.bps} bps` : ''}{p.re_split_percent ? `${p.re_split_percent}% split` : ''}
              {p.cap_per_deal ? ` · cap ${fmt$(p.cap_per_deal)}` : ''}
              {p.deductions_pct ? ` · ${p.deductions_pct}% ded.` : ''}
            </div>
          </>
        )} />
      ) : tab === 'permissions' ? (
        <PermissionsTab onDirtyChange={setPermsDirty} />
      ) : tab === 'programs' ? (
        <LoanProgramsTab />
      ) : tab === 'qualdefaults' ? (
        <QualificationDefaultsTab />
      ) : tab === 'notifications' ? (
        <NotificationsTab />
      ) : tab === 'usage' ? (
        canSeeAiUsage ? <AiUsageTab /> : null
      ) : tab === 'features' ? (
        canManageFeatures ? <FeaturesTab /> : null
      ) : tab === 'incident' ? (
        <IncidentTab />
      ) : tab === 'move' ? (
        canMoveDeals ? <MoveDealsTab companies={companySeats} /> : null
      ) : (
        <div className="space-y-4">
          <CommissionCalculator plans={plans} />
          <div className="flex items-center gap-3">
            <Input type="month" value={periodDraft} onChange={(e) => pickPeriod(e.target.value)} placeholder="YYYY-MM"
              aria-label="Commission month" className="w-44" data-testid="commissions-period" />
            <button onClick={exportCsv} disabled={exporting} data-testid="export-payroll-btn" className="btn-outline">
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export Payroll CSV
            </button>
          </div>
          {records.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-16 text-center text-slate-400">
              <DollarSign className="mx-auto mb-3 text-slate-300" size={32} />
              <p className="text-sm">No commission records for {period} yet</p>
            </div>
          ) : (
            <div className="relative bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Period</th>
                    <th className="px-4 py-3 text-right">Gross</th>
                    <th className="px-4 py-3 text-right">Bonus</th>
                    <th className="px-4 py-3 text-right">Deductions</th>
                    <th className="px-4 py-3 text-right">Net</th>
                    <th className="px-4 py-3 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r.record_id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-4" data-testid={`commission-user-${r.record_id}`}>
                        {r.user_name || r.user_email || 'Unknown user'}
                        {r.user_name && r.user_email && <div className="text-xs text-slate-500">{r.user_email}</div>}
                      </td>
                      <td className="px-4 py-4">{r.period}</td>
                      <td className="px-4 py-4 text-right">{fmt$(r.gross)}</td>
                      <td className="px-4 py-4 text-right">{fmt$(r.bonus)}</td>
                      <td className="px-4 py-4 text-right">{fmt$(r.deductions)}</td>
                      <td className="px-4 py-4 text-right font-medium" style={{ color: '#070B5F' }}>{fmt$(r.net)}</td>
                      <td className="px-4 py-4 text-right"><span className="text-xs text-slate-500">{r.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <BulkInviteDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={load}
        roles={INVITABLE_ROLES.filter((r) => canInvite || r !== 'admin')} />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ color: '#070B5F' }} className="font-serif">{tab === 'members' ? 'Invite a team member' : `New ${tab.slice(0, -1)}`}</DialogTitle></DialogHeader>
          {tab === 'members' && (
            <div className="space-y-3">
              <div><Label>Email</Label><Input type="email" value={addForm.email || ''} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} className="mt-1" data-testid="invite-email" /></div>
              <div>
                <Label>Role</Label>
                <Select value={addForm.role || 'loan_officer'} onValueChange={(v) => setAddForm({ ...addForm, role: v })}>
                  <SelectTrigger className="mt-1" data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INVITABLE_ROLES.filter(r => canInvite || r !== 'admin').map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-slate-500">They get an email with a personal link. Owners are promoted from the member list, never invited.</p>
            </div>
          )}
          {tab === 'branches' && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={addForm.name || ''} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="mt-1" /></div>
              <div><Label>City</Label><Input value={addForm.city || ''} onChange={(e) => setAddForm({ ...addForm, city: e.target.value })} className="mt-1" /></div>
              <div><Label>State</Label><Input value={addForm.state || ''} onChange={(e) => setAddForm({ ...addForm, state: e.target.value })} className="mt-1" /></div>
            </div>
          )}
          {tab === 'teams' && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={addForm.name || ''} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="mt-1" /></div>
              <div>
                <Label>Kind</Label>
                <Select value={addForm.kind || 'mortgage'} onValueChange={(v) => setAddForm({ ...addForm, kind: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mortgage">Mortgage</SelectItem>
                    <SelectItem value="real_estate">Real Estate</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {tab === 'plans' && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={addForm.name || ''} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="mt-1" /></div>
              <div>
                <Label>Division</Label>
                <Select value={addForm.division || 'mortgage'} onValueChange={(v) => setAddForm({ ...addForm, division: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mortgage">Mortgage</SelectItem>
                    <SelectItem value="real_estate">Real Estate</SelectItem>
                    <SelectItem value="any">Any</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Basis Points (mortgage)</Label><NumberInput integer value={addForm.bps} onChange={(n) => setAddForm({ ...addForm, bps: n })} className="mt-1" /></div>
              <div><Label>Cap per Deal</Label><MoneyInput value={addForm.cap_per_deal} onChange={(n) => setAddForm({ ...addForm, cap_per_deal: n })} className="mt-1" /></div>
              <div><Label>Deductions %</Label><NumberInput value={addForm.deductions_pct} onChange={(n) => setAddForm({ ...addForm, deductions_pct: n })} suffix="%" className="mt-1" /></div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setAddOpen(false)} className="btn-outline">Cancel</button>
            <button onClick={submit} className="btn-gold" data-testid="org-add-save">Save</button>
          </div>
        </DialogContent>
      </Dialog>

      <ChangeRoleDialog member={roleTarget} onClose={() => setRoleTarget(null)}
        onSaved={() => { setRoleTarget(null); load(); }} />

      <EditMemberDialog member={profileTarget} members={members} onClose={() => setProfileTarget(null)}
        onSaved={() => { setProfileTarget(null); load(); }} />

      <EditBranchDialog branch={branchTarget} staff={staff} onClose={() => setBranchTarget(null)}
        onSaved={() => { setBranchTarget(null); load(); }} />

      <EditTeamDialog team={teamTarget} branches={branches} staff={staff} onClose={() => setTeamTarget(null)}
        onSaved={() => { setTeamTarget(null); load(); }} />

      <EditPlanDialog plan={planTarget} onClose={() => setPlanTarget(null)}
        onSaved={() => { setPlanTarget(null); load(); }} />

      <TeamMembersDialog team={teams.find((t) => t.team_id === membersTeamId) || null} staff={staff}
        onClose={() => setMembersTeamId(null)} onChanged={load} />

      <AlertDialog open={!!killTarget} onOpenChange={(open) => { if (!open) setKillTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign {killTarget?.name} out everywhere?</AlertDialogTitle>
            <AlertDialogDescription>
              Ends every session on every device. If this is you, you will be signed out on your next action.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="org-kill-sessions-confirm" onClick={killMemberSessions} disabled={killBusy}>
              Sign out everywhere
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!statusTarget} onOpenChange={(open) => { if (!open) setStatusTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {nextStatusFor(statusTarget) === 'suspended' ? `Suspend ${statusTarget?.name || statusTarget?.email}?` : `Reactivate ${statusTarget?.name || statusTarget?.email}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {nextStatusFor(statusTarget) === 'suspended'
                ? 'A suspended seat cannot sign in to this workspace. Their data and assignments stay put; you can reactivate them any time. This is audited.'
                : 'They regain access to this workspace with their previous role. This is audited.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="org-status-confirm" onClick={setMemberStatus} disabled={statusBusy}
              className={nextStatusFor(statusTarget) === 'suspended' ? 'bg-red-600 hover:bg-red-700' : undefined}>
              {nextStatusFor(statusTarget) === 'suspended' ? 'Suspend' : 'Reactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!removeTarget} onOpenChange={(open) => { if (!open) setRemoveTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.name || removeTarget?.email} from {activeSeat?.name || 'this workspace'}?</AlertDialogTitle>
            <AlertDialogDescription>
              They lose access to this workspace right away; their own personal workspace is not affected. You can invite them again later. This is audited.
              {removeImpact > 0 && (
                <span className="block mt-2 font-medium text-slate-700" data-testid="org-remove-impact">
                  {removeImpact} deal{removeImpact === 1 ? ' is' : 's are'} still assigned to or owned by them — reassign {removeImpact === 1 ? 'it' : 'them'} from each deal.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="org-remove-confirm" onClick={removeMember} disabled={removeBusy}
              className="bg-red-600 hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!pendingTab} onOpenChange={(open) => { if (!open) setPendingTab(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved permission changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You changed the permissions grid but did not save. Leaving this tab throws those changes away.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="perm-leave-stay">Keep editing</AlertDialogCancel>
            <AlertDialogAction data-testid="perm-leave-discard" onClick={() => { setPermsDirty(false); setTab(pendingTab); setPendingTab(null); }}
              className="bg-red-600 hover:bg-red-700">
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!ownerTarget} onOpenChange={(open) => { if (!open) setOwnerTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Make {ownerTarget?.name} the owner?</AlertDialogTitle>
            <AlertDialogDescription>
              A workspace has exactly one owner. The current owner becomes an admin and keeps every permission except the locked owner row. This is audited.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="org-make-owner-confirm" onClick={transferOwnership} disabled={transferBusy}>
              Transfer ownership
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function List({ items, kind, onDel, onEdit, extraItems, render }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      {items.length === 0 ? (
        <div className="text-center py-16 text-slate-400">Nothing here yet</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map(i => {
            const id = i[`${kind}_id`]; // a team also carries branch_id, so never guess by field order
            return (
              <li key={id}
                className="px-4 py-4 flex items-center justify-between"
                data-testid={`${kind}-row`}>
                <div>{render(i)}</div>
                <RowMenu testid={`${kind}-menu-${id}`} items={[
                  ...(onEdit ? [{ key: 'edit', label: 'Edit', onClick: () => onEdit(i), testid: `${kind}-edit-${id}` }] : []),
                  ...(extraItems ? extraItems(i) : []),
                  { key: 'delete', label: 'Delete', onClick: () => onDel(kind, id), testid: `${kind}-delete-${id}`, danger: true },
                ]} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
